<template>
    <Row v-if="hasExportOptionSetting('dcProfileId')">
        <Col span="16">
            <h4>Profile ID</h4>
            <p class="tooltip">
                Before downloading your ads, set up your campaign feed in <strong>Google Studio</strong> to get your
                <strong>Profile ID</strong> and add it here.
            </p>
            <p class="error">{{ dcProfileIdError }}</p>
        </Col>
        <Col span="8">
            <Input
                v-model="dcProfileId"
                type="number"
                placeholder="Google Studio Profile ID"
                data-testid="download-modal__dc-profile-id-input"
            />
        </Col>
    </Row>

    <Divider v-if="hasExportOptionSetting('dcProfileId')" size="small" />

    <DownloadOptionWrapper v-if="hasExportOptionSetting('ftClickTagCount')" tooltip="Flashtalking Click tag count">
        <Checkbox v-model="ftClickTagCount.enabled" data-testid="download-modal__click-tag-count-checkbox">
            <h4>Click Tag Count</h4>
        </Checkbox>

        <template #input>
            <Input
                v-model="ftClickTagCount.value"
                data-testid="download-modal__click-tag-count-input"
                class="small-input"
            />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('ftBrowserExclusions')"
        tooltip="Comma separated list of browsers"
    >
        <Checkbox v-model="ftBrowserExclusions.enabled" data-testid="download-modal__browser-exclusions-checkbox">
            <h4>Browser Exclusions</h4>
        </Checkbox>
        <template #input>
            <Input v-model="ftBrowserExclusions.value" data-testid="download-modal__browser-exclusions-input" />
        </template>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('optimiseTextFiles')"
        tooltip="Minify HTML, CSS and JS files used by the template"
    >
        <Checkbox v-model="optimiseTextFiles" data-testid="download-modal__optimise-files-checkbox">
            <h4>Optimise Files</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('optimiseFontFiles')"
        tooltip="Remove unused glyphs from font files used in the template"
    >
        <Checkbox v-model="optimiseFontFiles" data-testid="download-modal__optimise-fonts-checkbox">
            <h4>Optimise Fonts</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('optimiseImages')"
        tooltip="This may result in color profile changes"
    >
        <Checkbox v-model="optimiseImages" data-testid="download-modal__optimise-images-checkbox">
            <h4>Optimise Images</h4>
        </Checkbox>
    </DownloadOptionWrapper>

    <DownloadOptionWrapper
        v-if="hasExportOptionSetting('includeBackupImage')"
        tooltip="Include backup images in the download"
    >
        <Checkbox v-model="includeBackupImage" data-testid="download-modal__backup-images-checkbox">
            <h4>Include backup images</h4>
        </Checkbox>
    </DownloadOptionWrapper>
</template>

<script lang="ts" setup>
import { watch } from "vue";
import {
    EXPORT_CONFIGS,
    type ExportOptionConfig,
    type ExportOptionSetting,
    type ExportFormatType
} from "hoxton-shared/enums/exports";

import DownloadOptionWrapper from "@/components/Download/DownloadOptionWrapper.vue";
import { useDownloadHTMLZip } from "@/composables/download/useDownloadHTMLZip";

const { exportFormat } = defineProps<{
    exportFormat: ExportFormatType;
}>();

const {
    optimiseFontFiles,
    optimiseTextFiles,
    optimiseImages,
    includeBackupImage,
    ftBrowserExclusions,
    ftClickTagCount,
    dcProfileId,
    dcProfileIdError,

    resetDownloadOptions
} = useDownloadHTMLZip();

watch(
    () => exportFormat,
    () => {
        resetDownloadOptions();
    }
);

function hasExportOptionSetting(setting: ExportOptionSetting): boolean {
    const exportConfig: ExportOptionConfig = EXPORT_CONFIGS[exportFormat];

    return exportConfig.settings?.includes(setting) ?? false;
}
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;
.tooltip {
    font-size: 12.5px;
}

.error {
    color: $error-color;
    font-size: 10px;
    height: 10px;
}
</style>
